import React from 'react';
import { Dialog, DialogContent, DialogActions, Typography, Button, Stack, Box, alpha } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { closeConfirmDialog } from '@store/appStore';
import { styled } from '@mui/material/styles';

const IOSDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 14,
        maxWidth: 450,
        width: '100%',
        backgroundColor: theme.palette.mode === 'dark' ? '#2c2c2e' : '#f2f2f7',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        background: theme.palette.mode === 'dark' ? 'rgba(44, 44, 46, 0.8)' : 'rgba(242, 242, 247, 0.8)',
        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.15)',
        overflow: 'hidden'
    },
    '& .MuiBackdrop-root': {
        backgroundColor: alpha('#000', 0.5),
        backdropFilter: 'blur(1px)',
        WebkitBackdropFilter: 'blur(4px)'
    }
}));

const DialogButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontSize: '18px',
    fontWeight: 600,
    borderRadius: 0,
    height: '56px',
    minHeight: '56px',
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08)
    },
    '&.cancel': {
        fontWeight: 400,
        color: theme.palette.text.secondary
    },
    '&.destructive': {
        color: '#ff3b30'
    },
    '&:not(:last-child)': {
        borderBottom: `0.5px solid ${alpha('#000', 0.1)}`
    },
    '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, 0.15)
    }
}));

const ContentDivider = styled(Box)(() => ({
    height: '0.5px',
    width: '100%',
    backgroundColor: alpha('#000', 0.1),
    margin: 0
}));

const IOSConfirmDialog: React.FC = () => {
    const dispatch = useDispatch();
    const { confirmDialog } = useSelector((state: RootReducer) => state.app);

    const handleClose = () => {
        dispatch(closeConfirmDialog());
    };

    const handleButtonClick = (buttonAction?: () => void) => {
        if (buttonAction) {
            buttonAction();
        }
        handleClose();
    };

    // Botones por defecto si no hay botones definidos
    const defaultButtons = [
        {
            text: 'OK',
            onPress: () => {},
            style: 'default' as const
        }
    ];

    // Usar botones definidos o los por defecto
    const buttonsToShow =
        confirmDialog.buttons && confirmDialog.buttons.length > 0 ? confirmDialog.buttons : defaultButtons;

    // Si no está abierto, no renderizar
    if (!confirmDialog.open) {
        return null;
    }

    const cancelButton = buttonsToShow.find((btn) => btn.style === 'cancel');
    const otherButtons = buttonsToShow.filter((btn) => btn.style !== 'cancel');

    return (
        <IOSDialog
            open={confirmDialog.open}
            onClose={handleClose}
            aria-labelledby="ios-confirm-dialog"
            disableEscapeKeyDown={!confirmDialog.hideCloseButton}
        >
            <DialogContent
                sx={{
                    textAlign: 'center',
                    p: 4
                }}
            >
                <Stack spacing={1}>
                    <Typography variant="h6" fontWeight="600" color="text.primary">
                        {confirmDialog.title}
                    </Typography>
                    {/* pre-line: los mensajes multilínea (listas de consecuencias) conservan
                        sus saltos; los de una línea se ven igual que siempre */}
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {confirmDialog.message}
                    </Typography>
                </Stack>
            </DialogContent>

            <ContentDivider />

            <DialogActions
                sx={{
                    flexDirection: 'column',
                    p: 0,
                    margin: 0
                }}
            >
                <Box sx={{ width: '100%' }}>
                    {otherButtons.map((button, index) => (
                        <DialogButton
                            key={`action-${index}`}
                            fullWidth
                            onClick={() => handleButtonClick(button.onPress)}
                            className={button.style || 'default'}
                            sx={{
                                borderRadius: 0,
                                '&:first-of-type': {
                                    borderTopLeftRadius: 0,
                                    borderTopRightRadius: 0
                                },
                                '&:last-of-type': {
                                    borderBottomLeftRadius:
                                        index === otherButtons.length - 1 && !cancelButton ? '14px' : 0,
                                    borderBottomRightRadius:
                                        index === otherButtons.length - 1 && !cancelButton ? '14px' : 0
                                }
                            }}
                        >
                            {button.text}
                        </DialogButton>
                    ))}
                    <DialogButton
                        fullWidth
                        onClick={() => (cancelButton ? handleButtonClick(cancelButton.onPress) : handleClose())}
                        className="cancel"
                        sx={{
                            borderRadius: 0,
                            '&:first-of-type': {
                                borderTopLeftRadius: 0,
                                borderTopRightRadius: 0
                            },
                            '&:last-of-type': {
                                borderBottomLeftRadius: '14px',
                                borderBottomRightRadius: '14px'
                            }
                        }}
                    >
                        {cancelButton?.text ?? 'Cancelar'}
                    </DialogButton>
                </Box>
            </DialogActions>
        </IOSDialog>
    );
};

export default IOSConfirmDialog;
